import React, { useState, useEffect } from 'react';
import { Sidebar } from '../Sidebar';
import { Navbar } from '../../Navbar';
import config from '../../../config/config';
import * as XLSX from 'xlsx'; // Import Excel Library

export function PaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Aaj ki date default rakhne ke liye ---
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // --- Excel Export Function ---
  const exportToExcel = () => {
    // 1. Data ko Excel ke format mein prepare karein
    const excelData = payments.map(row => ({
      Date: new Date(row.created_at).toLocaleDateString('en-GB'),
      Type: row.prefix?.trim(),
      Method: row.pay_method?.trim(),
      Debit: parseFloat(row.debit || 0),
      Credit: parseFloat(row.credit || 0),
      ModuleID: row.moduel_id
    }));

    // 2. Worksheet banayein
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payment History");

    // 3. File download karwaein
    XLSX.writeFile(workbook, `Payment_History_${new Date().toLocaleDateString()}.xlsx`);
  };

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true); // Loading start
      try {
        const token = localStorage.getItem('token');
        // --- Backend ko Query Params mein date bhej rahe hain ---
        const response = await fetch(`${config.baseUrl}/api/payment/paymentHistory?startDate=${startDate}&endDate=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();

        if (Array.isArray(result)) {
          setPayments(result);
        } else if (result && typeof result === 'object' && result.id) {
          setPayments([result]);
        } else if (result.data && Array.isArray(result.data)) {
          setPayments(result.data);
        } else {
          setPayments([]);
        }
      } catch (error) {
        console.error("Error:", error);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [startDate, endDate]); // Jab bhi date change hogi, fetchHistory chalega

  const totalDebit = payments.reduce((sum, row) => sum + parseFloat(row.debit || 0), 0);
  const totalCredit = payments.reduce((sum, row) => sum + parseFloat(row.credit || 0), 0);
  const netBalance = totalCredit - totalDebit;

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#434561]">Payment Ledger / History</h1>

          {/* --- Date Filter Inputs --- */}
          <div className="flex gap-2 mt-3">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border rounded px-2 py-1 text-sm shadow-sm focus:ring-2 focus:ring-green-400 outline-none"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border rounded px-2 py-1 text-sm shadow-sm focus:ring-2 focus:ring-green-400 outline-none"
            />
          </div>

          <button
            onClick={exportToExcel}
            className="mt-4 bg-[#76C085] hover:bg-green-700 text-white px-4 py-1.5 rounded text-sm font-bold shadow-sm transition-all"
          >
            Export to Excel (.xlsx)
          </button>
        </div>

        <div className="flex gap-3">
          <div className="bg-white px-4 py-2 rounded shadow-sm border-b-4 border-red-500">
            <p className="text-[10px] uppercase text-gray-400 font-bold">Total Out</p>
            <p className="text-lg font-bold text-red-600">{totalDebit.toLocaleString()}</p>
          </div>
          <div className="bg-white px-4 py-2 rounded shadow-sm border-b-4 border-green-500">
            <p className="text-[10px] uppercase text-gray-400 font-bold">Total In</p>
            <p className="text-lg font-bold text-green-600">{totalCredit.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <table className="min-w-full leading-normal">
          <thead>
            <tr className="bg-[#434561] text-white text-xs font-bold uppercase">
              <th className="px-6 py-4 text-left">Date</th>
              <th className="px-6 py-4 text-left">Type</th>
              <th className="px-6 py-4 text-left">Method</th>
              <th className="px-6 py-4 text-left">Debit</th>
              <th className="px-6 py-4 text-left">Credit</th>
              <th className="px-6 py-4 text-left">Module ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="6" className="text-center py-20 text-gray-400">Loading data for selected dates...</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-20 text-gray-400">No data found for these dates.</td></tr>
            ) : (
              <>
                {payments.map((row, index) => (
                  <tr key={index} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(row.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                      <span className="text-gray-400 ml-1">
                        {new Date(row.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[10px] font-bold rounded uppercase">
                        {row.prefix?.trim()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                      {row.pay_method?.trim()}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-red-500">
                      {parseFloat(row.debit) > 0 ? `-${parseFloat(row.debit).toLocaleString()}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-green-600">
                      {parseFloat(row.credit) >= 0 ? `+${parseFloat(row.credit).toLocaleString()}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">#{row.moduel_id}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-black border-t-2">
                  <td colSpan="3" className="px-6 py-5 text-right text-xs">GRAND TOTAL</td>
                  <td className="px-6 py-5 text-red-600">{totalDebit.toLocaleString()}</td>
                  <td className="px-6 py-5 text-green-700">{totalCredit.toLocaleString()}</td>
                  <td className="px-6 py-5 bg-gray-200 text-center font-bold">Net: {netBalance.toLocaleString()}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}